import { api } from './api'

/**
 * Hoá đơn điện tử khởi tạo từ máy tính tiền.
 *
 * Máy này không tự đánh số hoá đơn và không tự cấp mã cơ quan thuế — cả hai đều do nhà
 * cung cấp cấp, qua máy chủ. Việc ở đây chỉ là hỏi xin và hiển thị kết quả.
 *
 * Không có đường nào trong file này được phép chặn một lần bán. Mất mạng thì bỏ qua, hoá
 * đơn tự phát hành khi lượt đồng bộ sau đi lên.
 */

export type EInvoiceStatus = 'Pending' | 'Issued' | 'Failed' | 'Cancelled'

export interface EInvoiceView {
  orderId: string
  orderCode: string
  status: EInvoiceStatus
  templateCode: string | null
  series: string | null
  invoiceNo: string | null
  taxAuthorityCode: string | null
  lookupUrl: string | null
  issuedAt: string | null
  total: number
  buyerName: string | null
  buyerTaxCode: string | null
  attempts: number
  nextAttemptAt: string | null
  error: string | null
}

/** Cả năm ô đều để trống được — khách mua ly cà phê không ai khai mã số thuế. */
export interface EInvoiceBuyer {
  name?: string | null
  taxCode?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
}

export type EInvoiceSource = 'System' | 'Own'

export interface EInvoiceConfigView {
  enabled: boolean
  source: EInvoiceSource
  provider: string | null
  account: string | null
  hasSecret: boolean
  endpoint: string | null
  templateCode: string | null
  series: string | null
  autoIssue: boolean
  lastOkAt: string | null
  lastError: string | null
  /** sigoPOS đã mở dịch vụ chưa — chưa thì không chọn được kiểu "dùng tài khoản sigoPOS". */
  systemReady: boolean
  systemProvider: string | null
  providers: { key: string; label: string }[]
  pendingCount: number
  failedCount: number
  warning: string | null
}

export interface EInvoiceConfigSave {
  enabled: boolean
  source: EInvoiceSource
  provider: string | null
  account: string | null
  /** Để trống nghĩa là giữ nguyên mật khẩu đang lưu. Màn hình không bao giờ hiện lại nó. */
  secret: string | null
  endpoint: string | null
  templateCode: string | null
  series: string | null
  autoIssue: boolean
}

export const einvoiceApi = {
  config: () => api.get<EInvoiceConfigView>('/api/einvoice/config'),

  saveConfig: (v: EInvoiceConfigSave) => api.put<EInvoiceConfigView>('/api/einvoice/config', v),

  /**
   * Xin phát hành ngay. Trả null khi đơn chưa kịp đồng bộ lên máy chủ — không phải lỗi,
   * hàng đợi trên máy chủ sẽ nhận nó ở lượt đồng bộ sau.
   */
  async issue(orderId: string, buyer?: EInvoiceBuyer): Promise<EInvoiceView | null> {
    const res = await api.post<EInvoiceView | { message: string }>('/api/einvoice/issue', {
      orderId,
      buyer: buyer ?? null,
    })
    return res && 'orderId' in res ? res : null
  },

  forOrder: (orderId: string) => api.get<EInvoiceView>(`/api/einvoice/order/${orderId}`),

  list: (date?: string, status?: EInvoiceStatus) => {
    const q = new URLSearchParams()
    if (date) q.set('date', date)
    if (status) q.set('status', status)
    const s = q.toString()
    return api.get<EInvoiceView[]>(`/api/einvoice${s ? `?${s}` : ''}`)
  },

  retry: (orderId: string) => api.post<EInvoiceView>(`/api/einvoice/order/${orderId}/retry`),
}

/** Ký hiệu và số hoá đơn gộp lại thành thứ khách đọc được: "C25MAA số 00000123". */
export function invoiceLabel(e: {
  series: string | null
  invoiceNo: string | null
}): string | null {
  if (!e.series && !e.invoiceNo) return null
  if (!e.invoiceNo) return e.series
  return `${e.series ?? ''} số ${e.invoiceNo}`.trim()
}

export const statusText: Record<EInvoiceStatus, string> = {
  Pending: 'Đang chờ xuất',
  Issued: 'Đã xuất',
  Failed: 'Chưa xuất được',
  Cancelled: 'Đã huỷ',
}
